import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFirestore, collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import app from "./firebase";
import styles from "./IngredientUsageDetailDashboard.module.css";
import { format2 } from "./utils/format2";

const db = getFirestore(app);

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const d = toJsDate(value);
  return d ? d.toLocaleString() : "-";
};

const compareValues = (a, b) => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  const da = toJsDate(a);
  const dbb = toJsDate(b);
  if (da && dbb) return da.getTime() - dbb.getTime();

  const na = typeof a === "number" ? a : Number(a);
  const nb = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;

  return String(a).localeCompare(String(b));
};

export default function IngredientUsageDetailDashboard() {
  const { ingredientId } = useParams();
  const navigate = useNavigate();

  const [ingredient, setIngredient] = useState(null);
  const [usageRows, setUsageRows] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [ingSnap, usageSnap, lotsSnap] = await Promise.all([
          getDoc(doc(db, "ingredients", ingredientId)),
          getDocs(query(collection(db, "ingredient_usage_history"), where("ingredientId", "==", ingredientId))),
          getDocs(query(collection(db, "ingredient_inventory_lots"), where("ingredientId", "==", ingredientId)))
        ]);

        setIngredient(ingSnap.exists() ? { id: ingSnap.id, ...ingSnap.data() } : null);
        setUsageRows(usageSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLots(lotsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching ingredient usage:", err);
        setIngredient(null);
        setUsageRows([]);
        setLots([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [ingredientId]);

  const remainingTotal = useMemo(() => {
    return lots.reduce((sum, l) => sum + (Number(l.remainingQuantity) || 0), 0);
  }, [lots]);

  const filteredAndSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = usageRows.filter(r => {
      if (!term) return true;
      const hay = [r.productName, r.batchCode, r.recipeName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });

    const dir = sortDir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => dir * compareValues(a?.[sortField], b?.[sortField]));
  }, [usageRows, search, sortField, sortDir]);

  if (loading) return <p className={styles.loading}>Loading usage...</p>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{ingredient?.name || "Ingredient Usage"}</h2>
          <div className={styles.subtitle}>
            Remaining: {format2(remainingTotal)} {ingredient?.unit || ""}
          </div>
        </div>
        <button className={styles.backButton} onClick={() => navigate("/ingredient-usage")}>Back</button>
      </div>

      <div className={styles.controls}>
        <input
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search product or batch code..."
        />
        <select className={styles.select} value={sortField} onChange={(e) => setSortField(e.target.value)}>
          <option value="date">Date</option>
          <option value="productName">Product</option>
          <option value="batchCode">Batch</option>
          <option value="quantityUsed">Quantity Used</option>
          <option value="lotPurchaseDate">Purchase Date</option>
        </select>
        <select className={styles.select} value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
          <option value="desc">Desc</option>
          <option value="asc">Asc</option>
        </select>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Batch</th>
              <th>Quantity Used</th>
              <th>Purchase Date (FIFO lot)</th>
              <th>Lot</th>
              <th>Lot Remaining</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map(r => (
              <tr key={r.id}>
                <td>{formatDateTime(r.date)}</td>
                <td>{r.productName || "-"}</td>
                <td>{r.batchCode || "-"}</td>
                <td>{r.quantityUsed === null || r.quantityUsed === undefined ? "-" : `-${format2(Math.abs(Number(r.quantityUsed)))}`} {r.unit || ""}</td>
                <td>{formatDateTime(r.lotPurchaseDate)}</td>
                <td>{r.lotId ? String(r.lotId).slice(-6) : "-"}</td>
                <td>{r.lotRemainingAfter === null || r.lotRemainingAfter === undefined ? "-" : format2(r.lotRemainingAfter)} {r.unit || ""}</td>
              </tr>
            ))}
            {filteredAndSorted.length === 0 && (
              <tr><td className={styles.empty} colSpan={7}>No usage found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
